import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { AnchorCellContext } from '../table/anchorCell';
import { Checkbox } from '../inputs/Checkbox';
import { Fragment, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Skeleton } from '../Skeleton';
import type { OtherInfoData } from '../table/OtherInfoModal';
import { RowActionMenu, type MenuItem } from '../table/RowActionMenu';
import { RowQuickActions } from '../table/RowQuickActions';
import { quickActionsWidth } from '../table/rowQuickActionWidth';
import { useColumnWidths } from '../table/useColumnWidths';

/** Every gutter is chrome-owned and fixed — the reader resizes data columns only. */
const SELECT_GUTTER = 44;
const NUMBER_GUTTER = 56;

/**
 * The trailing actions gutter, at the width `<IndexTable>` pins its own kebab.
 *
 * It is CHROME, not a column, and that distinction is the whole point: a caller hands over
 * `rowActions` and the table places the kebab itself. While each call site declared an
 * `actions` column instead, `<DataTablePanel>`'s status hoist moved status straight past it
 * and every panel that flagged a status column rendered `… | ⋮ | Status`. CLAUDE.md has always
 * said status sits "before the Actions gutter"; this is the gutter it can now sit before.
 */
const ACTIONS_GUTTER = 56;

export interface DataColumn<T> {
  /** Doubles as the sort field and the width-persistence key, so it must be stable. */
  key: string;
  header: string;
  /** Default width in pixels. The user's dragged width wins once they have set one. */
  width: number;
  /** Omit for a column the server cannot order by — the header stays plain text. */
  sortable?: boolean;
  align?: 'left' | 'right';

  /**
   * The record's state. Moved to the end of the data columns, and rendered on the RIGHT of a
   * list row.
   *
   * `DataColumn` is meant to be structurally a `ColumnDef` — that is what lets `<IndexListView>`
   * take a panel's columns directly — and this was one of two fields where it was not.
   * `<IndexListView>` looks for `column.status` to fill the right-hand stack, so a panel table
   * could not render a status badge in its list view at all: the property did not exist to set.
   */
  status?: boolean;

  /**
   * The record's own LIFECYCLE — its stage in a workflow, beside the live-or-deleted `status`.
   *
   * <b>Added to BOTH types in one change, which is what this field's own history demands.</b>
   * `DataColumn` is meant to be structurally a `ColumnDef`, and `status` and `summary` were each
   * missing from it once — so a panel's list view could not draw a status badge at all, because
   * the property did not exist to set. A third field added to one and not the other reproduces
   * that exactly, and silently: the panel compiles, the table looks right, and the list view
   * quietly has nowhere to put the stage.
   *
   * See `ColumnDef.stage` for what it decides.
   */
  stage?: boolean;

  /**
   * Nominates this column for the list row's summary line, up to three.
   *
   * The other half of the same gap. Without it a panel's list view fell back to "the first few
   * columns after the title", which is a real default but not one the page can overrule — so a
   * leading column that is often empty spent a summary slot rendering an em dash.
   */
  summary?: boolean;

  /**
   * The BAND this column sits under, drawn as a second header row above the column headers.
   *
   * Opt-in and per column rather than a `bands` prop listing keys: one declaration for the
   * band AND the column is what stops a new column going missing from a hand-kept list —
   * the failure `<IndexPage headerGroups>` already names.
   *
   * A run of ADJACENT columns sharing a label is one band. A column declaring none spans both
   * header rows as one tall cell, which is what makes the bands read as bands rather than as
   * headings over empty space — and a table where no column declares one draws no band row at
   * all, so every existing panel is untouched.
   */
  group?: string;

  /**
   * This column's TOTALS cell. Any column declaring one turns the footer on for the table.
   *
   * **The same shape `<IndexTable>`'s takes**, deliberately — it receives the rows on screen
   * and returns what goes under them, so a panel and an index page total a column the same
   * way rather than two surfaces inventing two arrangements.
   *
   * It was listed as something only the index shell has, on the reasoning that a panel has
   * nowhere to put one. A panel has a `<tfoot>`; what it lacked was the prop. The department
   * PPA table is the first consumer — the reference draws a Total row under it, and a table of
   * money whose columns do not add up is one the reader has to add up themselves.
   */
  footer?: (rows: T[]) => ReactNode;

  render: (row: T) => ReactNode;
}

/**
 * A resizable, sortable table for a record list that is **not** an index page — a panel
 * inside a profile card, a sub-table in a detail view.
 *
 * Deliberately separate from `<IndexTable>`, which is the index-page shell: that one owns a
 * `#` gutter, multi-select, a filter rail and `useRowHeight`, whose grip hangs off that
 * gutter — none of which a panel has anywhere to put. Sharing one component would have meant
 * making half of it optional.
 *
 * The pinned Actions gutter, the audit trio and the TOTALS FOOTER were all once on that list
 * and are not: each turned out to be a prop this table lacked rather than something it could
 * not hold, and each cost a call site a hand-rolled copy before it was noticed.
 *
 * What the two DO share is the machinery: column widths come from the same
 * `useColumnWidths`, so a drag persists per table key to `localStorage` under the same rules
 * and there is no adjacent-column compensation.
 *
 * Search and paging are its siblings, not its job — see `<DataTablePanel>`, which wires all
 * four parts of the standard table contract together.
 */
/**
 * A row's ground — the row itself, or the pinned kebab cell that must repeat it (an opaque white
 * there shows a notch where the tint stops). ONE tone per class string: selection, a data-state
 * tint, or the plain hover wash.
 */
function rowGround(selected: boolean, tone: 'red' | null, part: 'row' | 'pinned'): string {
  if (selected) return part === 'row' ? 'bg-blue-50/40 hover:bg-slate-50' : 'bg-blue-50/40 group-hover:bg-slate-50';
  if (tone === 'red') return part === 'row' ? 'bg-red-50 hover:bg-red-100' : 'bg-red-50 group-hover:bg-red-100';
  return part === 'row' ? 'hover:bg-slate-50' : 'bg-white group-hover:bg-slate-50';
}

export function DataTable<T>({
  id,
  columns,
  rows,
  rowKey,
  sort,
  onToggleSort,
  loading = false,
  emptyMessage = 'Nothing to show.',
  minWidth = 420,
  fill = false,
  numbered = true,
  onRowOpen,
  rowActions,
  onEdit,
  editPermission,
  onDelete,
  deletePermission,
  deleteLabel,
  deleteDisabledReason,
  otherInfo,
  selection,
  firstRow = 1,
  rowTone,
  groupRow,
  rowQuickActions,
}: {
  /** DOM id prefix and the key column widths persist under. */
  id: string;
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** `field:asc` / `field:desc`, or null for the server's default order. */
  sort: string | null;
  onToggleSort: (field: string) => void;
  loading?: boolean;
  emptyMessage?: ReactNode;
  minWidth?: number;
  /**
   * Grow to fill the height on offer and scroll inside, rather than being as tall as its
   * rows. Set by `<DataTablePanel fill>`; see the anchoring rule there.
   */
  fill?: boolean;
  /** The `#` gutter. On by default; pass false for a table where position means nothing. */
  numbered?: boolean;
  /**
   * Makes the FIRST data column the row’s click target.
   *
   * Its absence is what makes a table read-only, exactly as it is on the index shell — a
   * panel that has nothing to open passes nothing and its first column stays plain text.
   */
  onRowOpen?: (row: T) => void;
  /**
   * The row's kebab, drawn in a pinned trailing gutter — never as a column a caller declares.
   *
   * Supplying it is what draws the gutter, so a table nothing can be done to passes nothing
   * and grows no column. `<RowActionMenu>` owns the menu, which is what gives a panel row the
   * same entries, the same order, the same permission filtering and the same right-click
   * shortcut as a row on an index page.
   */
  /**
   * A DATA-STATE TINT on a whole row — today only `'red'`, for a row that needs the reader's
   * attention (an evidence item not yet evaluated, 08 Oct 2026). The `<InlineRow className>`
   * precedent, on the read-only tables. ONE tone or the other, never both in one class string:
   * a tinted row SWAPS the hover wash for its own deeper step, and a selected row keeps the
   * selection wash. Omitted, every row is exactly what it was.
   */
  rowTone?: (row: T) => 'red' | null;
  /**
   * A GROUP HEADER ROW before each run of rows sharing a key — the evidence Stored in folder's
   * containers (08 Oct 2026). Full width, drawn whenever the key changes from the row above, so the
   * ROWS must arrive ordered by it (the server sorts; a column sort would split the groups). A page
   * that starts mid-group repeats its header. Table view only — the list view draws no headers.
   */
  groupRow?: { key: (row: T) => string; render: (row: T) => ReactNode };
  rowActions?: (row: T) => MenuItem[];
  /**
   * A row's most-used actions as icon buttons BEFORE the kebab — `<IndexTable>`'s `rowQuickActions`,
   * in a panel (09 Oct 2026). PROMOTED, never duplicated: the same `MenuItem`s, so permission and
   * disabled reason travel. Two or three; never destructive. The gutter grows a slot per entry.
   */
  rowQuickActions?: (row: T) => MenuItem[];
  /*
    The canonical Edit and Delete, in the fixed order `<RowActionMenu>` guarantees — Other
    Info, then the caller's extras, then Edit, then Delete last and destructive. Same names
    and same meanings as `<IndexTable>` and `<IndexListView>`, so `<DataTablePanel>` forwards
    one group to both surfaces with nothing to translate between them.
  */
  onEdit?: (row: T) => void;
  editPermission?: string;
  onDelete?: (row: T) => void;
  deletePermission?: string;
  deleteLabel?: (row: T) => string;
  /** Disables Delete with a reason rather than hiding it — for state, not permission. */
  deleteDisabledReason?: (row: T) => string | null;
  /**
   * The row's audit metadata, which fills the kebab's **Other Info** entry.
   *
   * Read only alongside the entries above — it is the menu's first item, not a gutter of its
   * own.
   */
  otherInfo?: (row: T) => OtherInfoData | null;
  /**
   * The select gutter. Omitted, no checkbox column is drawn.
   *
   * `<DataTablePanel>` supplies this by default — a caller opts OUT with
   * `selectable={false}` rather than opting in, so an embedded table looks like the index
   * table unless there is a reason for it not to.
   */
  selection?: {
    isSelected: (row: T) => boolean;
    onToggle: (row: T) => void;
    onToggleAll: () => void;
    allSelected: boolean;
    someSelected: boolean;
  };
  /** The number the first row on this page carries, so paging keeps counting. */
  firstRow?: number;
}) {
  const { widths, startResize } = useColumnWidths(id, columns);

  const [sortField, sortDirection] = sort ? sort.split(':') : [null, null];

  // Drawn only for a table that has something to do to a row: a kebab opening an empty menu
  // is the dead affordance the hide-rule forbids.
  const hasActions =
    rowActions !== undefined || onEdit !== undefined || onDelete !== undefined || rowQuickActions !== undefined;

  // The gutter is sized for what is IN it — the kebab plus a slot per quick action, counted BEFORE
  // permission filtering (over-wide is invisible, under-wide is a button that cannot be pressed).
  const quickGutter =
    rowQuickActions && rows.length > 0 ? Math.max(...rows.map((row) => quickActionsWidth(rowQuickActions(row)))) : 0;
  const actionsGutter = ACTIONS_GUTTER + quickGutter;

  /*
    THE BAND ROW's runs, derived ONCE and read by both header rows.

    A run is a stretch of ADJACENT columns carrying the same `group`; a column with none is a
    run of one that spans both rows. Deriving it twice — once for the band row and once for a
    "does this column sit under a band" filter on the row below — is two answers to one
    question, and they agree right up until a band holds a single column.
  */
  const runs: { label: string | undefined; keys: string[] }[] = [];

  for (const column of columns) {
    const last = runs[runs.length - 1];

    if (last !== undefined && last.label !== undefined && last.label === column.group) {
      last.keys.push(column.key);
    } else {
      runs.push({ label: column.group, keys: [column.key] });
    }
  }

  const hasBands = runs.some((run) => run.label !== undefined);

  /** One column declaring a footer turns the totals row on for the whole table. */
  const hasFooter = columns.some((column) => column.footer);

  /** The columns genuinely under a band — the ones the row below draws normally. */
  const banded = new Set(
    runs.filter((run) => run.label !== undefined).flatMap((run) => run.keys),
  );

  /*
    A BAND BOUNDARY is a column whose LEFT rule closes one region and opens another, and it is
    drawn one step above the grid around it on EVERY row — the band cell, the header beneath
    it, each body row and the totals footer — so the boundary is ONE continuous line down the
    table rather than a dark stub in the band row.

    BOTH edges of a band count: the first column of a band, and the first column of whatever
    follows one.

    Drawn as a LEFT rule only, never a right one as well. Two adjacent cells each declaring a
    rule puts 2px in the gap — `border-collapse` does not merge them, because they belong to
    different cells and both are declared. That is the defect `<StockLevelsIndexTable>` already
    fixed once, and this is the same rule arriving on the shared shell.
  */
  const boundary = new Set<string>();

  runs.forEach((run, index) => {
    if (run.label !== undefined || runs[index - 1]?.label !== undefined) {
      boundary.add(run.keys[0]!);
    }
  });

  /*
    The table's own LEADING edge, resolved by POSITION rather than by `:first-child`.

    `first:border-l-0` is scoped to the ROW, and in a banded table the second header row does
    NOT start with the table's first column — it starts with the first column of the first
    band, which is a boundary. So the pseudo-class stripped the rule from exactly the cell that
    most needed one: the band's leading edge ran down the band cell and stopped dead at the
    header below it. A line that stops halfway, and nothing fails.
  */
  const leadingKey = selection || numbered ? undefined : columns[0]?.key;

  /*
    ONE GRID TONE FOR THE WHOLE TABLE, and ONE step for a band boundary.

    Every vertical rule is `slate-200` — header, body and totals footer alike — and a band
    boundary is `slate-300` the whole height. That is `<StockLevelsIndexTable>`'s scheme
    exactly, which is the product's other banded table.

    It was THREE tones: slate-300 in the header, slate-200 in the body, and a boundary that
    was slate-400 over the header and slate-300 over the rows. Measured on a rendered page,
    EVERY vertical line on the table changed colour partway down — an ordinary column rule
    carried two values from the header into the body, and the boundary carried three.

    The header/body split came from the two-shells rule, which reads a darker header rule as
    part of the grid both shells share. It is not worth a table whose own lines disagree with
    themselves: a grid is one grid. `<IndexTable>` still draws its header at slate-300, so
    the two shells now differ by that one step — see CLAUDE.md, where it is recorded rather
    than left to be found.
  */
  const GRID_RULE = 'border-l-slate-200';
  const BOUNDARY_RULE = 'border-l-slate-300';

  const headerRule = (key: string) => (boundary.has(key) ? BOUNDARY_RULE : GRID_RULE);

  const bodyRule = (key: string) => (boundary.has(key) ? BOUNDARY_RULE : GRID_RULE);

  /** A column's position, which is what `useColumnWidths` and the resize grip are keyed on. */
  const columnIndex = (key: string) => columns.findIndex((column) => column.key === key);

  // The table is exactly as wide as its columns add up to; `minWidth: 100%` then stretches a
  // narrow one to fill the panel rather than leaving a gap on the right.
  const totalWidth =
    columns.reduce((sum, column, index) => sum + (widths[index] ?? column.width), 0) +
    (hasActions ? actionsGutter : 0);

  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 px-3 py-6 text-center text-sm text-slate-500">
        {emptyMessage}
      </p>
    );
  }

  return (
    // `modern-scrollbar` is load-bearing, not decoration: the resize hook measures this
    // element to decide how far a column may be dragged.
    <div
      className={`modern-scrollbar rounded-lg border border-slate-200 ${
        fill ? 'min-h-0 flex-1 overflow-auto' : 'overflow-x-auto'
      }`}
    >
      {/*
        STRETCHED TO THE PANEL ONLY WHERE THERE IS A STICKY TFOOT TO HOLD ON THE BOTTOM EDGE —
        `<IndexTable>`'s own arrangement, and it was the half this shell was missing.

        `sticky bottom-0` alone pins nothing on a SHORT table: a sticky element sits at its
        natural position until its container actually scrolls, so a two-line Items tab in a
        647px panel left the total floating mid-panel with 499px of white under it — measured,
        and it is the screen the product owner photographed.

        `height: 100%` on a table hands its surplus to the ROW boxes, distributed across them,
        so the stretch is conditional and the spacer row at the end of the tbody absorbs the
        slack instead. The rows keep the height they asked for and the total sits on the edge.
      */}
      <table
        id={id}
        className="border-collapse text-left"
        style={{
          width: totalWidth,
          minWidth: `max(100%, ${minWidth}px)`,
          tableLayout: 'fixed',
          height: hasFooter ? '100%' : undefined,
        }}
      >
        <colgroup>
          {/* Chrome-owned and fixed: the reader never resizes a gutter. */}
          {selection ? <col style={{ width: SELECT_GUTTER }} /> : null}
          {numbered ? <col style={{ width: NUMBER_GUTTER }} /> : null}
          {columns.map((column, index) => (
            <col key={column.key} style={{ width: widths[index] ?? column.width }} />
          ))}
          {hasActions ? <col style={{ width: actionsGutter }} /> : null}
        </colgroup>

        {/*
          The same header as `<IndexTable>`, down to the class string: a `bg-slate-100` band,
          9px uppercase in `gray-600`, and the weight on the CELLS rather than the row —
          `<th>` carries the browser's own bold and Tailwind's preflight does not reset it, so
          a weight set here would never reach them.

          A table inside a profile card is still a table of records. A reader moving from a
          module index to one of these should not meet a second header treatment, which is
          the same reasoning that makes the two shells share `useColumnWidths`, `<SearchGroup>`
          and `<IndexPagination>`.
        */}
        <thead>
          {/*
            THE BAND ROW. Drawn only when a column declares a `group`, so every panel that
            does not is byte-for-byte what it was.

            A column belonging to no band is a TALL cell spanning both rows rather than a cell
            under an empty one — that is what makes the bands read as bands. Its header is
            therefore rendered HERE and skipped below, which is why the two rows share one set
            of runs.
          */}
          {hasBands ? (
            <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
              {selection ? (
                /*
                  `align-bottom` with the tall data headers beside it. A gutter spanning both
                  rows and left at the default `middle` sits a row-height above every column
                  name on the same row, which is the disagreement this row exists to avoid.
                */
                <th
                  scope="col"
                  rowSpan={2}
                  className="border-l border-slate-200 bg-slate-100 px-3 py-2 align-bottom first:border-l-0"
                >
                  <Checkbox
                    id={`${id}-select-all`}
                    checked={selection.allSelected}
                    indeterminate={selection.someSelected && !selection.allSelected}
                    onChange={selection.onToggleAll}
                    aria-label="Select all rows on this page"
                  />
                </th>
              ) : null}

              {numbered ? (
                <th
                  scope="col"
                  rowSpan={2}
                  className="border-l border-slate-200 bg-slate-100 px-3 py-2 text-right align-bottom first:border-l-0"
                >
                  #
                </th>
              ) : null}

              {runs.map((run) =>
                run.label === undefined ? (
                  /*
                    No band: the column's own header, as one TALL cell — and it carries the
                    sort button and the resize grip, because the row below skips it entirely.
                    Dropping either here would silently take sorting and resizing away from
                    every unbanded column the moment a table grew its first band.
                  */
                  <HeaderCell<T>
                    key={run.keys[0]}
                    id={id}
                    column={columns[columnIndex(run.keys[0]!)]!}
                    index={columnIndex(run.keys[0]!)}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onToggleSort={onToggleSort}
                    startResize={startResize}
                    rowSpan={2}
                    rule={headerRule(run.keys[0]!)}
                    leading={run.keys[0] === leadingKey}
                  />
                ) : (
                  <th
                    key={`band-${run.label}-${run.keys[0]}`}
                    scope="colgroup"
                    colSpan={run.keys.length}
                    /*
                      The band's own OUTER edge is a step darker than the grid it bounds, so a
                      band reads as one region rather than as a run of columns that happen to
                      share a word. Drawn on the leading edge only; the next band draws its own.

                      CENTRED over its run, not left-aligned. The columns under a money band are
                      right-aligned, so a label pinned to the far left read as belonging to the
                      first of them rather than to all four.
                    */
                    className={[
                      'border-l bg-slate-100 px-3 py-2 text-center',
                      run.keys[0] === leadingKey ? 'border-l-0' : BOUNDARY_RULE,
                    ].join(' ')}
                  >
                    {run.label}
                  </th>
                ),
              )}

              {hasActions ? (
                <th
                  scope="col"
                  rowSpan={2}
                  className="sticky right-0 z-20 border-l border-slate-200 bg-slate-100 px-2 py-2 text-right"
                  style={{ width: actionsGutter }}
                >
                  <span className="sr-only">Actions</span>
                </th>
              ) : null}
            </tr>
          ) : null}

          <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
            {selection && !hasBands ? (
              <th
                scope="col"
                className="border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0"
              >
                <Checkbox
                  id={`${id}-select-all`}
                  checked={selection.allSelected}
                  indeterminate={selection.someSelected && !selection.allSelected}
                  onChange={selection.onToggleAll}
                  aria-label="Select all rows on this page"
                />
              </th>
            ) : null}

            {numbered && !hasBands ? (
              <th
                scope="col"
                className="border-l border-slate-200 bg-slate-100 px-3 py-2 text-right first:border-l-0"
              >
                #
              </th>
            ) : null}

            {columns.map((column, index) => {
              // A column with no band is drawn as a TALL cell in the row above, so it is
              // skipped here — the two rows read one set of runs and cannot disagree.
              if (hasBands && !banded.has(column.key)) return null;

              return (
                <HeaderCell<T>
                  key={column.key}
                  id={id}
                  column={column}
                  index={index}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onToggleSort={onToggleSort}
                  startResize={startResize}
                  rule={headerRule(column.key)}
                  leading={column.key === leadingKey}
                  topRule={hasBands}
                />
              );
            })}

            {/*
              Actions is last, fixed, and pinned to the right edge — `<IndexTable>`'s gutter in
              a panel. Pinning is not decoration here either: this table scrolls sideways the
              moment a column is widened, and an unpinned kebab ends up off-screen, which is
              the primary way to act on the row.

              The heading is `sr-only`. It is chrome, and a visible one would read as a data
              column the reader can sort or drag.
            */}
            {hasActions && !hasBands ? (
              <th
                scope="col"
                className="sticky right-0 z-20 border-l border-slate-200 bg-slate-100 px-2 py-2 text-right"
                style={{ width: actionsGutter }}
              >
                <span className="sr-only">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => (
            <Fragment key={rowKey(row)}>
            {groupRow && (index === 0 || groupRow.key(rows[index - 1]) !== groupRow.key(row)) ? (
              <tr className="border-t border-slate-200 bg-slate-50">
                <td
                  colSpan={(selection ? 1 : 0) + (numbered ? 1 : 0) + columns.length + (hasActions ? 1 : 0)}
                  className="px-3 py-2 align-middle"
                >
                  {groupRow.render(row)}
                </td>
              </tr>
            ) : null}
            {/*
              `border-t` per row and `border-l` per cell, both `slate-100` — the index table's
              grid exactly. The column rules are the half that was missing: without them a
              value ran into its neighbour and the table read as a list of sentences rather
              than as a grid to scan down.

              `align-middle`, not `align-top`, for the same reason the index table uses it: a
              row's cells sit on one line, and top-aligning them makes a wrapped cell drag the
              short ones up to its first line.
            */}
            <tr
              className={`group border-t border-slate-200 transition ${rowGround(
                selection?.isSelected(row) ?? false,
                rowTone?.(row) ?? null,
                'row',
              )}`}
            >
              {selection ? (
                <td className="border-l border-slate-200 px-3 py-2 align-middle first:border-l-0">
                  <Checkbox
                    id={`${id}-select-${rowKey(row)}`}
                    checked={selection.isSelected(row)}
                    onChange={() => selection.onToggle(row)}
                    aria-label="Select row"
                  />
                </td>
              ) : null}

              {numbered ? (
                <td className="border-l border-slate-200 px-3 py-2 text-right align-middle font-mono tabular-nums text-xs text-slate-400 first:border-l-0">
                  {firstRow + index}
                </td>
              ) : null}

              {columns.map((column, columnIndex) => (
                <td
                  key={column.key}
                  className={[
                    /*
                      THE TONE IS THE SHELL'S. With none set the cell INHERITS `body`'s `#0f172a` —
                      the value this product reserves for whatever is SELECTED — so any column whose
                      `render` returns a bare string painted darker than the heading above it, and
                      read as deliberate because every such row agreed. `<TextCell>` sets its own tone
                      on the span inside and still wins; this is the floor for the renderers that set
                      none. `<PreviewGrid>` already took this fix; the two table shells had not.
                    */
                    'border-l px-3 py-2 align-middle text-slate-500 first:border-l-0',
                    bodyRule(column.key),
                    column.align === 'right' ? 'text-right' : '',
                  ].join(' ')}
                >
                  {/*
                    The FIRST data column is the row’s click target when the panel supplies
                    `onRowOpen` — the index table’s anchor, in a panel. Same treatment for
                    the same reason: it stopped being just an identifier, so the weight and
                    the tone are the affordance.

                    The tone comes from `AnchorCellContext`, never from a class here. This
                    button carried `font-medium text-slate-800` of its own while the panel's
                    OWN list view — the canonical `<IndexListView>` — provided the context and
                    rendered the anchor blue, so switching view inside one panel changed the
                    colour of the same click target. One provider, both surfaces.

                    A button rather than a click handler on the `<tr>`: a row is not a
                    control, and making one swallows every selection drag and every click
                    on the kebab inside it.
                  */}
                  {onRowOpen && columnIndex === 0 ? (
                    <AnchorCellContext.Provider value={true}>
                      <button
                        type="button"
                        onClick={() => onRowOpen(row)}
                        className="block w-full min-w-0 cursor-pointer rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                      >
                        {column.render(row)}
                      </button>
                    </AnchorCellContext.Provider>
                  ) : (
                    column.render(row)
                  )}
                </td>
              ))}

              {/*
                The pinned cell repeats the row's OWN ground, or a selected or hovered row
                shows a white notch where the kebab sits — the tint stops at the sticky cell
                because that cell paints over it. Mirrored from the `<tr>` above rather than
                guessed, so the two cannot disagree about what a hovered selected row looks
                like.
              */}
              {hasActions ? (
                <td
                  className={`sticky right-0 z-10 border-l border-slate-200 px-2 py-2 text-right align-middle ${rowGround(
                    selection?.isSelected(row) ?? false,
                    rowTone?.(row) ?? null,
                    'pinned',
                  )}`}
                >
                  <div className="flex items-center justify-end gap-1">
                    {rowQuickActions ? <RowQuickActions id={`${id}-quick-${rowKey(row)}`} items={rowQuickActions(row)} /> : null}
                    <RowActionMenu
                      id={`${id}-actions-${rowKey(row)}`}
                      otherInfo={otherInfo ? otherInfo(row) : null}
                      extraItems={rowActions?.(row)}
                      onEdit={onEdit ? () => onEdit(row) : undefined}
                      editPermission={editPermission}
                      onDelete={onDelete ? () => onDelete(row) : undefined}
                      deletePermission={deletePermission}
                      deleteLabel={deleteLabel?.(row)}
                      deleteDisabledReason={deleteDisabledReason?.(row) ?? null}
                    />
                  </div>
                </td>
              ) : null}
            </tr>
            </Fragment>
          ))}

          {/*
            Takes the stretch that would otherwise inflate every data row. Only present while
            the table is stretched at all, so an ordinary panel carries no phantom row.

            ONE CELL PER COLUMN, never a `colSpan` — the grid has to run to the footer. A single
            spanning cell draws no vertical rules, so the column lines stopped under the last row
            and the band above the totals was blank. Each cell mirrors the BODY row's own border
            resolution, `bodyRule` included, so a band boundary carries its heavier tone down the
            stretch rather than reverting to the grid rule halfway.
          */}
          {hasFooter && !loading ? (
            <tr aria-hidden="true" className="h-full">
              {selection ? <td className="border-l border-slate-200 p-0 first:border-l-0" /> : null}
              {numbered ? <td className="border-l border-slate-200 p-0 first:border-l-0" /> : null}
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`border-l p-0 first:border-l-0 ${bodyRule(column.key)}`}
                />
              ))}
              {hasActions ? (
                <td className="sticky right-0 z-10 border-l border-slate-200 bg-white p-0" />
              ) : null}
            </tr>
          ) : null}
        </tbody>

        {/*
          THE TOTALS ROW, and it is `<IndexTable>`'s markup rather than a second arrangement —
          the same tinted band, the same uppercase chrome, the same gutter cells left empty
          because a sum of row numbers means nothing.

          REGULAR WEIGHT, and it is NOT bold — product call, 29 Sep 2026. `font-semibold` was
          here and it genuinely applied (a `<td>` carries no user-agent bold, unlike the `<th>`
          this shell's own header documents), so dropping it was a real change rather than the
          removal of an inert class. What separates the row is the tint and the rule, which is
          the table-display label column's own argument: the ground IS the treatment.

          Hidden while LOADING: a total under skeleton rows is a figure for a set of rows that
          is not on screen.
        */}
        {hasFooter && !loading ? (
          <tfoot className="sticky bottom-0 z-10">
            <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600">
              {selection ? (
                <td className="border-l border-slate-200 px-3 py-2 first:border-l-0" />
              ) : null}

              {numbered ? (
                <td className="border-l border-slate-200 px-3 py-2 first:border-l-0" />
              ) : null}

              {columns.map((column) => (
                <td
                  key={column.key}
                  className={[
                    'border-l px-3 py-2 first:border-l-0',
                    headerRule(column.key),
                    column.align === 'right' ? 'text-right' : 'text-left',
                  ].join(' ')}
                >
                  {column.footer?.(rows)}
                </td>
              ))}

              {hasActions ? (
                <td className="sticky right-0 z-10 border-l border-slate-200 bg-slate-100 px-2 py-2" />
              ) : null}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

/** Direction is shown, not just "sortable" — an arrow that never changes says nothing. */
/**
 * One column header — the label, its sort control and its resize grip.
 *
 * Its own component because a banded table draws an unbanded column in the TOP row and a
 * banded one in the row below, and those two call sites have to be the same cell. Written
 * inline at both, an unbanded column loses its sort button the first time somebody adds a
 * band to a table — which is silent in review, because the header still reads correctly.
 */
function HeaderCell<T>({
  id,
  column,
  index,
  sortField,
  sortDirection,
  onToggleSort,
  startResize,
  rowSpan,
  rule,
  leading = false,
  topRule = false,
}: {
  id: string;
  column: DataColumn<T>;
  index: number;
  sortField: string | null;
  sortDirection: string | null;
  onToggleSort: (field: string) => void;
  startResize: (index: number, event: ReactPointerEvent) => void;
  rowSpan?: number;
  /** The resolved left-rule tone — a boundary is a step above the grid it bounds. */
  rule: string;
  /** At the table's own leading edge, so it draws no left rule. Never `:first-child`. */
  leading?: boolean;
  /** Attaches a leaf header to the band above it. */
  topRule?: boolean;
}) {
  const active = sortField === column.key;

  return (
    <th
      scope="col"
      rowSpan={rowSpan}
      aria-sort={active ? (sortDirection === 'desc' ? 'descending' : 'ascending') : 'none'}
      className={[
        'relative border-l bg-slate-100 px-3 py-2',
        leading ? 'border-l-0' : rule,

        /*
          The TOP rule is what attaches a leaf header to its band, and it is drawn on the CHILD
          rather than as a bottom border on the band cell so it spans exactly the columns the
          band covers. Without it the label and its sub-headers sat in one undivided block and
          the band stopped reading as a band.
        */
        topRule ? 'border-t border-t-slate-300' : '',

        /*
          A cell spanning BOTH header rows sits on the same line as the leaf headers beside it,
          not in the middle of the pair. Left at the default `middle` an unbanded column's name
          floated a row-height above every banded one, so no two column names in the header
          shared a baseline.
        */
        rowSpan === 2 ? 'align-bottom' : 'align-middle',
        column.align === 'right' ? 'text-right' : 'text-left',
      ].join(' ')}
    >
      {/*
        Size, case and tracking come from the ROW, exactly as they do on the index table — a
        header cell that restated them is a second place for the two tables to drift apart.
      */}
      {column.sortable ? (
        <button
          type="button"
          id={`${id}-sort-${column.key}`}
          onClick={() => onToggleSort(column.key)}
          /*
            The glyph sits on the column's OUTER RIGHT — see `<IndexTable>`, which draws the
            same header. A right-aligned column keeps `justify-end` so its label stays right
            with the glyph outermost.
          */
          className={[
            'flex w-full items-center gap-1 uppercase tracking-wider transition',
            column.align === 'right' ? 'justify-end' : 'justify-between',
            active ? 'text-slate-900' : 'hover:text-slate-700',
          ].join(' ')}
        >
          <span className="min-w-0">{column.header}</span>
          <SortGlyph active={active} direction={sortDirection} />
        </button>
      ) : (
        // Not sortable: plain text, never a button that does nothing.
        column.header
      )}

      {/* Drag handle on the right edge. No adjacent-column compensation — the table widens
          instead, which is what makes a drag feel predictable. */}
      <span
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${column.header}`}
        onPointerDown={(e) => startResize(index, e)}
        className="absolute -right-0.5 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-blue-300"
      />
    </th>
  );
}

function SortGlyph({ active, direction }: { active: boolean; direction: string | null }) {
  if (!active) {
    return <ArrowUpDown className="h-3 w-3 shrink-0 text-slate-300" aria-hidden="true" />;
  }

  return direction === 'desc' ? (
    <ArrowDown className="h-3 w-3 shrink-0 text-slate-700" aria-hidden="true" />
  ) : (
    <ArrowUp className="h-3 w-3 shrink-0 text-slate-700" aria-hidden="true" />
  );
}
